import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ClipboardList, Pin } from "lucide-react";
import { Req, SectionHeader, AUTO_INPUT_CLS, FieldError, invalidCls } from "@/components/newTicket/parts";

export default function QueryDetailsSection({
  form, setForm,
  mainCats, subCats, issueTypes,
  autoFields, branches, validation = {},
}) {
  const err = (k) => validation.err?.(k);
  const touch = (k) => validation.touch?.(k);
  return (
    <section data-testid="section-query-details">
      <SectionHeader
        letter="C" title="Query Details" icon={ClipboardList}
        subtitle="Main Category first, all fields update automatically"
      />
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 mb-5 flex items-start gap-2.5">
        <Pin className="w-4 h-4 text-indigo-600 mt-0.5 shrink-0" />
        <p className="text-sm text-indigo-900 leading-relaxed">
          Select <strong>Main Category</strong> first. Sub-Category, Issue Type, Branch/Location, Priority and Department auto-populate based on your selection.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <Label className="text-xs font-semibold text-slate-700">Main Category<Req /></Label>
          <Select
            value={form.main_category_id}
            onValueChange={(v) => { setForm({ ...form, main_category_id: v, sub_category_id: "", issue_type_id: "", branch: "" }); touch("main_category_id"); }}
          >
            <SelectTrigger className={`mt-1.5 h-10 ${invalidCls(err("main_category_id"))}`} data-testid="main-category-select" onBlur={() => touch("main_category_id")}>
              <SelectValue placeholder="Select main category" />
            </SelectTrigger>
            <SelectContent>
              {mainCats.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError name="main_category_id" msg={err("main_category_id")} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Sub-Category<Req /></Label>
          <Select
            value={form.sub_category_id}
            onValueChange={(v) => { setForm({ ...form, sub_category_id: v, issue_type_id: "" }); touch("sub_category_id"); }}
            disabled={!form.main_category_id}
          >
            <SelectTrigger className={`mt-1.5 h-10 ${invalidCls(err("sub_category_id"))}`} data-testid="sub-category-select" onBlur={() => touch("sub_category_id")}>
              <SelectValue placeholder={form.main_category_id ? "Select sub-category" : "Select main first"} />
            </SelectTrigger>
            <SelectContent>
              {subCats.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError name="sub_category_id" msg={err("sub_category_id")} />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Issue Type<Req /></Label>
          <Select
            value={form.issue_type_id}
            onValueChange={(v) => { setForm({ ...form, issue_type_id: v }); touch("issue_type_id"); }}
            disabled={!form.sub_category_id}
          >
            <SelectTrigger className={`mt-1.5 h-10 ${invalidCls(err("issue_type_id"))}`} data-testid="issue-type-select" onBlur={() => touch("issue_type_id")}>
              <SelectValue placeholder={form.sub_category_id ? "Select issue type" : "Select sub-category first"} />
            </SelectTrigger>
            <SelectContent>
              {issueTypes.map((i) => (
                <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError name="issue_type_id" msg={err("issue_type_id")} />
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-700">Case Type <span className="text-slate-400 font-normal">(auto)</span></Label>
          <Input
            readOnly
            data-testid="case-type-auto"
            className={`mt-1.5 h-10 ${AUTO_INPUT_CLS}`}
            value={autoFields.case_type_name || ""}
            placeholder="auto from category"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Department <span className="text-slate-400 font-normal">(auto)</span></Label>
          <Input
            readOnly
            data-testid="department-auto"
            className={`mt-1.5 h-10 ${AUTO_INPUT_CLS}`}
            value={autoFields.department_name || ""}
            placeholder="auto"
          />
        </div>

        {autoFields.branch_required && (
          <div>
            <Label className="text-xs font-semibold text-slate-700">Branch / Location<Req /></Label>
            <Select value={form.branch} onValueChange={(v) => { setForm({ ...form, branch: v }); touch("branch"); }}>
              <SelectTrigger className={`mt-1.5 h-10 ${invalidCls(err("branch"))}`} data-testid="branch-select" onBlur={() => touch("branch")}>
                <SelectValue placeholder="-- Select Branch --" />
              </SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError name="branch" msg={err("branch")} />
          </div>
        )}
      </div>
    </section>
  );
}
